import { FC, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ProfitIcon, LossIcon, Close } from '../../../assets/icons';
import { classNames, roundNumber } from '../../../utils';
import { LabelledPeriodReturn } from '../../../hooks/usePortfolioReturns';
import { MarketInsight, PortalHoldingsInsights } from '../hooks/usePortalHoldings';

interface InsightModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  insights: MarketInsight[];
  formatCurrency: (value: number) => string;
  showRating?: boolean;
  showBid?: boolean;
}

const InsightModal: FC<InsightModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  insights,
  formatCurrency,
  showRating,
  showBid,
}) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">{title}</Dialog.Title>
              {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full"
            >
              <Close className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {insights.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No wines in this category</p>
            ) : (
              <div className="space-y-3">
                {insights.map((insight) => {
                  const isPositive = insight.changePercent >= 0;
                  return (
                    <div
                      key={insight.lwin18}
                      className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                    >
                      <div className="flex-1 min-w-0 pr-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {insight.vintage} {insight.wineName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{insight.region}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {showRating && insight.rating && (
                          <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-semibold rounded-full">
                            {insight.rating}
                          </span>
                        )}
                        {showBid && insight.bidPrice && (
                          <span className="text-xs text-gray-600 dark:text-gray-300">
                            {formatCurrency(insight.bidPrice)}
                          </span>
                        )}
                        <div
                          className={classNames(
                            'flex items-center gap-1',
                            isPositive ? 'text-trendup' : 'text-trenddown',
                          )}
                        >
                          {isPositive ? <ProfitIcon className="w-3 h-3" /> : <LossIcon className="w-3 h-3" />}
                          <span className="text-sm font-medium">
                            {isPositive ? '+' : ''}
                            {roundNumber(insight.changePercent, 1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

interface MetricChipProps {
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'neutral';
  count?: number;
  onClick?: () => void;
  accentColor?: string;
}

const MetricChip: FC<MetricChipProps> = ({ label, value, trend, count, onClick, accentColor }) => {
  const isClickable = !!onClick && (count === undefined || count > 0);
  // A chip with nothing to drill into is still a real figure - only fade the ones that offered
  // a wine list and came back empty, so the headline numbers do not read as disabled.
  const isEmptyDrilldown = !!onClick && !isClickable;

  return (
    <button
      type="button"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      className={classNames(
        'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors flex-shrink-0',
        isClickable
          ? 'hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer border-gray-200 dark:border-slate-600'
          : 'cursor-default border-gray-100 dark:border-slate-700',
        isEmptyDrilldown ? 'opacity-60' : '',
      )}
    >
      {accentColor && <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }} />}
      <div className="text-left">
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <div className="flex items-center gap-1">
          {trend &&
            trend !== 'neutral' &&
            (trend === 'up' ? <ProfitIcon className="w-3 h-3" /> : <LossIcon className="w-3 h-3" />)}
          <span
            className={classNames(
              'text-sm font-semibold',
              trend === 'up' ? 'text-trendup' : trend === 'down' ? 'text-trenddown' : 'text-gray-900 dark:text-white',
            )}
          >
            {value}
          </span>
          {count !== undefined && count > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
              {count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

interface MarketInsightsPanelProps {
  insights: PortalHoldingsInsights;
  /** Actual portfolio return per period, from the valuation series. */
  portfolioReturns?: LabelledPeriodReturn[];
  isLoadingReturns?: boolean;
  formatCurrency: (value: number) => string;
  className?: string;
}

type ModalType = 'momentum' | 'gainers' | 'opportunities' | 'rated' | 'declining' | null;

const MarketInsightsPanel: FC<MarketInsightsPanelProps> = ({
  insights,
  portfolioReturns,
  isLoadingReturns,
  formatCurrency,
  className,
}) => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  if (insights.isLoading) {
    return (
      <div className={classNames('flex gap-2 overflow-x-auto pb-2', className || '')}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-14 w-28 bg-gray-100 dark:bg-slate-700 rounded-lg animate-pulse flex-shrink-0" />
        ))}
      </div>
    );
  }

  const hasPortfolioReturn = (portfolioReturns ?? []).some((r) => r.percent !== null);
  const hasAnyInsights =
    hasPortfolioReturn ||
    insights.strongMomentum.length > 0 ||
    insights.recentGainers.length > 0 ||
    insights.highlyRated.length > 0 ||
    insights.bidOpportunities.length > 0 ||
    insights.winePriceChange12Months !== null;

  if (!hasAnyInsights && !isLoadingReturns) {
    return null;
  }

  const formatPercent = (value: number | null) =>
    value === null ? 'n/a' : `${value >= 0 ? '+' : ''}${roundNumber(value, 1)}%`;

  // Portfolio return leads, because that is what a client means by "how am I doing". The wine
  // price chips that follow measure something narrower - the market price of the wines held
  // today - and are labelled so the two are not confused. See helpers/portfolioReturns.
  const returnFor = (months: number) => portfolioReturns?.find((r) => r.months === months) ?? null;
  const return1M = returnFor(1);
  const return12M = returnFor(12);

  return (
    <>
      <div className={classNames('flex gap-2 overflow-x-auto pb-2', className || '')}>
        {isLoadingReturns ? (
          <>
            <div className="h-14 w-28 bg-gray-100 dark:bg-slate-700 rounded-lg animate-pulse flex-shrink-0" />
            <div className="h-14 w-28 bg-gray-100 dark:bg-slate-700 rounded-lg animate-pulse flex-shrink-0" />
          </>
        ) : (
          <>
            <MetricChip
              label="Portfolio 12M"
              value={formatPercent(return12M?.percent ?? null)}
              trend={(return12M?.percent ?? 0) >= 0 ? 'up' : 'down'}
              accentColor="#0f766e"
            />
            <MetricChip
              label="Portfolio 1M"
              value={formatPercent(return1M?.percent ?? null)}
              trend={(return1M?.percent ?? 0) >= 0 ? 'up' : 'down'}
              accentColor="#14b8a6"
            />
          </>
        )}
        <MetricChip
          label="Wine Prices 12M"
          value={formatPercent(insights.winePriceChange12Months)}
          trend={(insights.winePriceChange12Months ?? 0) >= 0 ? 'up' : 'down'}
          count={insights.strongMomentum.length}
          onClick={() => setActiveModal('momentum')}
          accentColor="#16a34a"
        />
        <MetricChip
          label="Wine Prices 1M"
          value={formatPercent(insights.winePriceChange1Month)}
          trend={(insights.winePriceChange1Month ?? 0) >= 0 ? 'up' : 'down'}
          count={insights.recentGainers.length}
          onClick={() => setActiveModal('gainers')}
          accentColor="#22c55e"
        />
        <MetricChip
          label="Sell Opps"
          value={insights.bidOpportunities.length.toString()}
          count={insights.bidOpportunities.length}
          onClick={() => setActiveModal('opportunities')}
          accentColor="#f59e0b"
        />
        <MetricChip
          label="Highly Rated"
          value={insights.avgRating > 0 ? roundNumber(insights.avgRating, 0).toString() : '-'}
          count={insights.highlyRated.length}
          onClick={() => setActiveModal('rated')}
          accentColor="#7c3aed"
        />
        {insights.weakMomentum.length > 0 && (
          <MetricChip
            label="Declining"
            value={insights.weakMomentum.length.toString()}
            trend="down"
            count={insights.weakMomentum.length}
            onClick={() => setActiveModal('declining')}
            accentColor="#ef4444"
          />
        )}
      </div>

      {/* Modals */}
      <InsightModal
        isOpen={activeModal === 'momentum'}
        onClose={() => setActiveModal(null)}
        title="Strong Performers"
        subtitle="12-month gains over 10%"
        insights={insights.strongMomentum}
        formatCurrency={formatCurrency}
      />
      <InsightModal
        isOpen={activeModal === 'gainers'}
        onClose={() => setActiveModal(null)}
        title="Recent Movers"
        subtitle="Gained over 3% this month"
        insights={insights.recentGainers}
        formatCurrency={formatCurrency}
      />
      <InsightModal
        isOpen={activeModal === 'opportunities'}
        onClose={() => setActiveModal(null)}
        title="Sell Opportunities"
        subtitle="Active bids above your cost"
        insights={insights.bidOpportunities}
        formatCurrency={formatCurrency}
        showBid
      />
      <InsightModal
        isOpen={activeModal === 'rated'}
        onClose={() => setActiveModal(null)}
        title="Highly Rated"
        subtitle="Wine Advocate 95+ points"
        insights={insights.highlyRated}
        formatCurrency={formatCurrency}
        showRating
      />
      <InsightModal
        isOpen={activeModal === 'declining'}
        onClose={() => setActiveModal(null)}
        title="Underperforming"
        subtitle="12-month decline over 5%"
        insights={insights.weakMomentum}
        formatCurrency={formatCurrency}
      />
    </>
  );
};

export default MarketInsightsPanel;
